import React, { useEffect, useMemo, useState, memo } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import api from "../api/axiosConfig";
import { useAuth } from '../context/AuthContext';
import { useBillingAccess } from '../hooks/useBillingAccess';
import { hasModule } from "../utils/moduleGating";
import { isAccountingClient } from "../utils/tenantType";
import { getVendorTerms } from "../utils/terminology";
import { prefetchRoute } from "../utils/routePrefetch";
import {
  HomeIcon,
  ShoppingCartIcon,
  DocumentTextIcon,
  UsersIcon,
  ClockIcon,
  Square3Stack3DIcon,
  CubeIcon,
  TagIcon,
  CogIcon,
  BuildingStorefrontIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PowerIcon,
  UserGroupIcon,
  ChartBarIcon,
  ChartBarSquareIcon,
  BanknotesIcon,
  ClipboardDocumentListIcon,
  TruckIcon,
  ArchiveBoxIcon,
  CalculatorIcon,
  ArrowLeftIcon,
  CreditCardIcon,
  DocumentCheckIcon,
  ReceiptRefundIcon,
  InboxArrowDownIcon,
  TableCellsIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  ArrowUpTrayIcon,
  CurrencyDollarIcon,
  PrinterIcon,
  DocumentChartBarIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";

// NOTE: Memoized menu item components to prevent unnecessary re-renders
const NavLinkItem = memo(({ path, title, icon, isOpen, onMouseEnter, end = false }) => (
  <NavLink
    to={path}
    end={end}
    onMouseEnter={onMouseEnter}
    className={({ isActive }) =>
      `flex items-center p-2 rounded-lg hover:bg-gray-700 transition-colors ${
        isActive ? "bg-gray-700" : ""
      } ${!isOpen ? "justify-center" : "space-x-2"}`
    }
  >
    {icon}
    {isOpen && <span>{title}</span>}
  </NavLink>
));
NavLinkItem.displayName = 'NavLinkItem';

const ReportLinkItem = memo(({ path, title, icon, isOpen, isActive, onMouseEnter }) => (
  <Link
    to={path}
    onMouseEnter={onMouseEnter}
    className={`flex items-center p-2 rounded-lg hover:bg-gray-700 transition-colors ${
      isActive ? "bg-gray-700" : ""
    } ${!isOpen ? "justify-center" : "space-x-2"}`}
  >
    {icon}
    {isOpen && <span>{title}</span>}
  </Link>
));
ReportLinkItem.displayName = 'ReportLinkItem';

const Sidebar = ({ role, mobileOpen = false, onMobileClose = () => {} }) => {
  const navigate = useNavigate();
  const { getAccessToken, isAuthenticated, logout } = useAuth();
  const { isMainStore } = useBillingAccess();
  const [isOpen, setIsOpen] = useState(true);
  const location = useLocation();

  // Close mobile sidebar on route change
  useEffect(() => {
    onMobileClose();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  const currentReportTab = new URLSearchParams(location.search).get('report') || 'trial-balance';

  // NOTE: Quick load - Initialize tenant from localStorage if available
  const [tenant, setTenant] = useState(() => {
    try {
      const cached = localStorage.getItem('cachedTenant');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [tenantLoading, setTenantLoading] = useState(false);

  // NOTE: Initialize expandedSections from localStorage cache — default all sections open
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const cached = localStorage.getItem('sidebarSections');
      if (cached) return JSON.parse(cached);
    } catch {}
    // Default: all sections open
    return {
      accounting_client: true,
      point_of_sale: true,
      accounting: true,
      inventory: true,
      purchasing: true,
      employees: true,
      reports: true,
      accounting_reports: true,
      settings: true,
      operations: true,
      ao_operations: true,
      // billing tenant type sections
      billing_accounting: true,
      billing_operations: true,
      billing_freight: true,
      billing_trucking: true,
      billing_reports: true,
      billing_setup: true,
      // property management sections
      pm_operations: true,
      pm_reports: true,
      pm_settings: true,
    };
  });

  useEffect(() => {
    if (!isAuthenticated || !getAccessToken()) return;
    const loadTenant = async () => {
      try {
        const res = await api.get("/me");
        // Expecting: { tenant: { tenantType, enabledModules, ... } }
        const tenantData = res?.data?.tenant || null;
        if (tenantData) {
          setTenant(tenantData);
          // Cache for quick load on next visit
          localStorage.setItem('cachedTenant', JSON.stringify(tenantData));
        }
      } catch (e) {
        console.error("Failed to load tenant from /me:", e);
        // Keep cached tenant even if request fails
      } finally {
        setTenantLoading(false);
      }
    };
    loadTenant();
  }, [isAuthenticated]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => {
      const next = { ...prev, [section]: !prev[section] };
      localStorage.setItem('sidebarSections', JSON.stringify(next));
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  // -----------------------------
  // Menu definitions
  // -----------------------------
  const commonMenu = useMemo(() => {
    if (tenant?.tenantType === 'accounting_operations') {
      return [];
    }

    const dashboardPath = tenant?.tenantType === 'accounting_operations' ? '/ao' : '/client-dashboard';

    return [
      {
        title: "Dashboard",
        path: dashboardPath,
        icon: <HomeIcon className="h-5 w-5" />,
        roles: ["superadmin", "admin", "manager", "cashier"],
      },
    ];
  }, [tenant]);

  // mrwd calls an accounting Vendor a "Supplier" — see utils/terminology.js.
  const vendorTerms = getVendorTerms(tenant?.tenantType);

  const roleBasedMenu = {
    superadmin: [
      {
        section: "Administration",
        items: [
          {
            title: "Session Management",
            path: "/superadmin/sessions",
            icon: <ShieldCheckIcon className="h-5 w-5" />,
          },
          {
            title: "ATC Codes",
            path: "/admin/atc-codes",
            icon: <TableCellsIcon className="h-5 w-5" />,
          },
        ],
      },
      {
        section: "Settings",
        items: [
          {
            title: "System Settings",
            path: "/settings",
            icon: <CogIcon className="h-5 w-5" />,
          },
        ],
      },
    ],

    admin: [
      {
        section: "Accounting Client",
        key: "accounting_client",
        items: [
          {
            title: "Accounting Clients",
            path: "/client/accounting-clients",
            icon: <UserGroupIcon className="h-5 w-5" />,
          },
          {
            title: "Tax Obligation",
            path: "/client/tax-obligation",
            icon: <DocumentTextIcon className="h-5 w-5" />,
          },

          {
            title: vendorTerms.many,
            path: "/client/vendors",
            icon: <UserGroupIcon className="h-5 w-5" />,
          },
          { title: "Customers", 
            path: "/customer", 
            icon: <UserGroupIcon className="h-5 w-5" /> 
          },
        ],
      },

      {
        section: "Point of Sale",
        key: "point_of_sale",
        items: [
          { title: "POS", path: "/pos", icon: <BuildingStorefrontIcon className="h-5 w-5" />, tenantTypes: ['restaurant'], end: true },
          { title: "Menu", path: "/pos/menu", icon: <Squares2X2Icon className="h-5 w-5" />, tenantTypes: ['restaurant'] },
          { title: "Inventory", path: "/pos/inventory", icon: <InboxArrowDownIcon className="h-5 w-5" />, tenantTypes: ['restaurant'] },
          { title: "Z / X Reading", path: "/reports/z-reading", icon: <ChartBarIcon className="h-5 w-5" />, tenantTypes: ['restaurant'] },
          { title: "P-Mix Report", path: "/pos/menu-engineering", icon: <DocumentChartBarIcon className="h-5 w-5" />, tenantTypes: ['restaurant'] },
          { title: "Customer", path: "/customer", icon: <UserGroupIcon className="h-5 w-5" /> },
          { title: "Sales Orders", path: "/sales-order", icon: <ClipboardDocumentListIcon className="h-5 w-5" /> },
          { title: "Invoices", path: "/invoice", icon: <DocumentTextIcon className="h-5 w-5" /> },
          { title: "Tables", path: "/client/tables", icon: <Square3Stack3DIcon className="h-5 w-5" /> },
        ],
      },

      {
        section: "Purchasing",
        key: "purchasing",
        items: [
          { title: "Purchase Orders", path: "/purchase-order", icon: <ClipboardDocumentListIcon className="h-5 w-5" /> },
          { title: "Goods Receipts", path: "/goods-receipts", icon: <InboxArrowDownIcon className="h-5 w-5" />, retailOnly: true },
          { title: "Supplier Invoices", path: "/supplier-invoices", icon: <DocumentCheckIcon className="h-5 w-5" />, retailOnly: true },
          { title: "Credit Notes", path: "/credit-notes", icon: <ReceiptRefundIcon className="h-5 w-5" />, retailOnly: true },
          { title: "Suppliers", path: "/suppliers", icon: <TruckIcon className="h-5 w-5" /> },
        ],
      },

      {
        section: "Accounting",
        key: "accounting",
        items: [
          { title: "Journal Entries", path: "/journal-entry", icon: <BanknotesIcon className="h-5 w-5" /> },
          { title: "Disbursements", path: "/disbursements", icon: <CreditCardIcon className="h-5 w-5" /> },
          { title: "Chart of Accounts", path: "/client/chart-of-accounts", icon: <Square3Stack3DIcon className="h-5 w-5" /> },
          { title: "COA Types", path: "/client/chart-of-accounts-types", icon: <ClipboardDocumentListIcon className="h-5 w-5" /> },
          { title: "General Ledger", path: "/client/general-ledger", icon: <CalculatorIcon className="h-5 w-5" /> },
        ],
      },

      {
        section: "Inventory",
        key: "inventory",
        items: [
          { title: "Products", path: "/client/products", icon: <CubeIcon className="h-5 w-5" /> },
          { title: "Product Categories", path: "/client/product-category", icon: <TagIcon className="h-5 w-5" /> },
          { title: "Inventory Management", path: "/inventory", icon: <ArchiveBoxIcon className="h-5 w-5" />, retailOnly: true },
        ],
      },

      {
        section: "Employees",
        key: "employees",
        items: [
          { title: "Employee Management", path: "/client/employees", icon: <UsersIcon className="h-5 w-5" /> },
          { title: "Attendance", path: "/attendance-list", icon: <ClockIcon className="h-5 w-5" /> },
        ],
      },

      {
        section: "Reports",
        key: "reports",
        items: [
          { title: "Accounting Reports", path: "/accounting-reports", icon: <ChartBarSquareIcon className="h-5 w-5" /> },
        ],
      },

      {
        section: "Accounting Reports",
        key: "accounting_reports",
        items: [
          { title: "Trial Balance", path: "/accounting-reports?report=trial-balance", icon: <DocumentTextIcon className="h-5 w-5" />, reportId: "trial-balance" },
          { title: "General Ledger", path: "/accounting-reports?report=general-ledger", icon: <CalculatorIcon className="h-5 w-5" />, reportId: "general-ledger" },
          { title: "Balance Sheet", path: "/accounting-reports?report=balance-sheet", icon: <Square3Stack3DIcon className="h-5 w-5" />, reportId: "balance-sheet" },
          { title: "Income Statement", path: "/accounting-reports?report=income-statement", icon: <ChartBarIcon className="h-5 w-5" />, reportId: "income-statement" },
          { title: "Cash Flow", path: "/accounting-reports?report=cash-flow", icon: <BanknotesIcon className="h-5 w-5" />, reportId: "cash-flow" },
          { title: "VAT Report", path: "/accounting-reports?report=vat-report", icon: <DocumentTextIcon className="h-5 w-5" />, reportId: "vat-report" },
          { title: "AR Aging", path: "/accounting-reports?report=ar-aging", icon: <DocumentTextIcon className="h-5 w-5" />, reportId: "ar-aging" },
          { title: "AP Aging", path: "/accounting-reports?report=ap-aging", icon: <DocumentTextIcon className="h-5 w-5" />, reportId: "ap-aging" },
        ],
      },

      {
        section: "Settings",
        key: "settings",
        items: [
          { title: "Stores", path: "/client/stores", icon: <BuildingStorefrontIcon className="h-5 w-5" /> },
          { title: "ATC Codes", path: "/admin/atc-codes", icon: <TableCellsIcon className="h-5 w-5" />, tenantTypes: ['accounting_firm'] },
          // mrwd only. Every tenant-type branch in filteredRoleMenu honours tenantTypes.
          { title: "Banks", path: "/settings/banks", icon: <BanknotesIcon className="h-5 w-5" />, tenantTypes: ['mrwd'] },
          { title: "Voucher", path: "/settings/voucher", icon: <DocumentCheckIcon className="h-5 w-5" />, tenantTypes: ['mrwd'] },
        ],
      },
      // ── Billing tenant type sections ──
      {
        section: "Accounting",
        key: "billing_accounting",
        items: [
          { title: "Chart of Accounts",  path: "/client/chart-of-accounts",                   icon: <Square3Stack3DIcon className="h-5 w-5" /> },
          { title: "Journal Entries",    path: "/journal-entry",                              icon: <BanknotesIcon className="h-5 w-5" /> },
          { title: "General Ledger",     path: "/client/general-ledger",                      icon: <CalculatorIcon className="h-5 w-5" /> },
          { title: "Trial Balance",      path: "/accounting-reports?report=trial-balance",    icon: <DocumentTextIcon className="h-5 w-5" />, reportId: "trial-balance" },
          { title: "Income Statement",   path: "/accounting-reports?report=income-statement", icon: <ChartBarIcon className="h-5 w-5" />, reportId: "income-statement" },
          { title: "Balance Sheet",      path: "/accounting-reports?report=balance-sheet",    icon: <Square3Stack3DIcon className="h-5 w-5" />, reportId: "balance-sheet" },
          { title: "Cash Flow",          path: "/accounting-reports?report=cash-flow",        icon: <BanknotesIcon className="h-5 w-5" />, reportId: "cash-flow" },
        ],
      },
      {
        section: "Freight Forwarding",
        key: "billing_freight",
        items: [
          { title: "Waybill",            path: "/billing/waybill",    icon: <DocumentTextIcon className="h-5 w-5" /> },
          { title: "FF Billing",         path: "/billing/ff-billing", icon: <CurrencyDollarIcon className="h-5 w-5" /> },
        ],
      },
      {
        section: "Trucking",
        key: "billing_trucking",
        items: [
          { title: "Trucking Waybill",   path: "/billing/trucking-waybill", icon: <DocumentTextIcon className="h-5 w-5" /> },
          { title: "Trucking Billing",   path: "/billing/trucking-billing", icon: <TruckIcon className="h-5 w-5" /> },
        ],
      },
      {
        section: "Reports",
        key: "billing_reports",
        items: [
          { title: "General Ledger", path: "/accounting-reports?report=general-ledger", icon: <CalculatorIcon className="h-5 w-5" />, reportId: "general-ledger" },
          { title: "Daily Waybill Report",   path: "/billing/reports/daily-waybill",     icon: <DocumentTextIcon className="h-5 w-5" /> },
          { title: "Daily Bill Report",       path: "/billing/reports/daily-bill",        icon: <DocumentTextIcon className="h-5 w-5" /> },
          { title: "Daily Trip Report",      path: "/billing/reports/daily-trip",        icon: <TableCellsIcon className="h-5 w-5" /> },
          { title: "Driver Payout",          path: "/billing/reports/driver-payout",     icon: <CurrencyDollarIcon className="h-5 w-5" /> },
          { title: "Revenue Per Van",        path: "/billing/reports/revenue-per-van",   icon: <TruckIcon className="h-5 w-5" /> },
          { title: "Undelivered Freight Forwarding", path: "/billing/reports/undelivered-freight-forwarding", icon: <DocumentTextIcon className="h-5 w-5" /> },
          { title: "PSACC Billing",          path: "/billing/reports/psacc-billing",     icon: <TableCellsIcon className="h-5 w-5" /> },
          { title: "PSACC Billing (Strip)",  path: "/billing/reports/psacc-strip",       icon: <TableCellsIcon className="h-5 w-5" /> },
          { title: "PSACC SOA Registry",     path: "/billing/reports/psacc-soa-registry", icon: <TableCellsIcon className="h-5 w-5" /> },
          { title: "Petty Cash Report",      path: "/billing/reports/petty-cash",        icon: <DocumentChartBarIcon className="h-5 w-5" /> },
          { title: "Daily Collection",      path: "/billing/reports/daily-collection", icon: <InboxArrowDownIcon className="h-5 w-5" /> },
          { title: "Accounts Receivable",   path: "/billing/ar",               icon: <BanknotesIcon className="h-5 w-5" /> },
          { title: "AR Aging",              path: "/billing/ar-aging",         icon: <ChartBarIcon className="h-5 w-5" /> },
        ],
      },
      {
        section: "Setup",
        key: "billing_setup",
        items: [
          { title: "Unit of Measure",      path: "/billing/setup/unit-of-measure",  icon: <Squares2X2Icon className="h-5 w-5" /> },
          { title: "Drivers",              path: "/billing/setup/drivers",           icon: <TruckIcon className="h-5 w-5" /> },
          {
            title: vendorTerms.many,
            path: "/client/vendors",
            icon: <UserGroupIcon className="h-5 w-5" />,
          },
          { title: "Customers",
            path: "/customer",
            icon: <UserGroupIcon className="h-5 w-5" />
          },
          { title: "System Settings",
            path: "/billing/settings",
            icon: <UserGroupIcon className="h-5 w-5" />
          },
        ],
      },
      // ── Property Management sections (visible only for property_management tenantType) ──
      {
        section: "Property Management",
        key: "pm_operations",
        items: [
          { title: "Lessee Directory",    path: "/property/lessees",   icon: <UsersIcon className="h-5 w-5" /> },
          { title: "Invoices",            path: "/property/invoices",  icon: <DocumentTextIcon className="h-5 w-5" /> },
          { title: "Payment Log",         path: "/property/payments",  icon: <BanknotesIcon className="h-5 w-5" /> },
        ],
      },
      {
        section: "PM Reports",
        key: "pm_reports",
        items: [
          { title: "Daily Collection",    path: "/property/reports/daily-collection",  icon: <InboxArrowDownIcon className="h-5 w-5" /> },
          { title: "Monthly Summary",     path: "/property/reports/monthly-lessee",    icon: <TableCellsIcon className="h-5 w-5" /> },
          { title: "AR Aging",            path: "/property/reports/ar-aging",          icon: <DocumentTextIcon className="h-5 w-5" /> },
          { title: "Lessee Ledger",       path: "/property/reports/lessee-ledger",     icon: <DocumentTextIcon className="h-5 w-5" /> },
          { title: "Accumulated Unpaid",  path: "/property/reports/accumulated-unpaid",icon: <DocumentTextIcon className="h-5 w-5" /> },
        ],
      },
      {
        section: "PM Settings",
        key: "pm_settings",
        items: [
          { title: "System Settings",     path: "/property/settings",                  icon: <CogIcon className="h-5 w-5" /> },
        ],
      },
      // ── Accounting Operations section (visible only for accounting_operations tenantType) ──
      {
        section: "AO Operations",
        key: "ao_operations",
        items: [
          { title: "Dashboard",            path: "/ao",                          icon: <HomeIcon className="h-5 w-5" /> },
          { title: "Clients",              path: "/ao/clients",                  icon: <UserGroupIcon className="h-5 w-5" /> },
          { title: "Transaction Entry",    path: "/ao/transactions",             icon: <ClipboardDocumentListIcon className="h-5 w-5" /> },
          { title: "Working Paper",        path: "/ao/report/working-paper",     icon: <TableCellsIcon className="h-5 w-5" /> },
          { title: "Collection Monitor",   path: "/ao/report/collection",        icon: <ChartBarIcon className="h-5 w-5" /> },
          { title: "Recurring PF",         path: "/ao/recurring",                icon: <ArrowPathIcon className="h-5 w-5" /> },
          { title: "Staff",                path: "/ao/staff",                    icon: <UsersIcon className="h-5 w-5" /> },
          { title: "Land Title Monitor",   path: "/ao/land-titles",              icon: <DocumentCheckIcon className="h-5 w-5" /> },
          { title: "Disbursement Entry",   path: "/ao/disbursements",            icon: <CreditCardIcon className="h-5 w-5" /> },
          { title: "Collection Entry",     path: "/ao/collections",              icon: <BanknotesIcon className="h-5 w-5" /> },
          { title: "Petty Cash",           path: "/ao/petty-cash",               icon: <BanknotesIcon className="h-5 w-5" /> },
          { title: "Petty Cash Report",    path: "/ao/petty-cash-report",        icon: <DocumentTextIcon className="h-5 w-5" /> },
          { title: "Daily Collection",     path: "/ao/daily-report",             icon: <DocumentTextIcon className="h-5 w-5" /> },
          { title: "Daily Expenses",       path: "/ao/expenses-report",          icon: <ReceiptRefundIcon className="h-5 w-5" /> },
          
        ],
      },    ],

    manager: [
      {
        section: "Point of Sale",
        key: "point_of_sale",
        items: [
          { title: "POS", path: "/pos", icon: <BuildingStorefrontIcon className="h-5 w-5" />, tenantTypes: ['restaurant'], end: true },
          { title: "Menu", path: "/pos/menu", icon: <Squares2X2Icon className="h-5 w-5" />, tenantTypes: ['restaurant'] },
          { title: "Inventory", path: "/pos/inventory", icon: <InboxArrowDownIcon className="h-5 w-5" />, tenantTypes: ['restaurant'] },
          { title: "POS Terminal", path: "/sales/new", icon: <ShoppingCartIcon className="h-5 w-5" /> },
          { title: "Sales Orders", path: "/sales-order", icon: <ClipboardDocumentListIcon className="h-5 w-5" /> },
          { title: "Invoices", path: "/invoice", icon: <DocumentTextIcon className="h-5 w-5" /> },
        ],
      },
      {
        section: "Inventory",
        key: "inventory",
        items: [
          { title: "Products", path: "/client/products", icon: <CubeIcon className="h-5 w-5" /> },
          { title: "Inventory Management", path: "/inventory", icon: <ArchiveBoxIcon className="h-5 w-5" />, retailOnly: true },
        ],
      },
      {
        section: "Purchasing",
        key: "purchasing",
        items: [
          { title: "Purchase Orders", path: "/purchase-order", icon: <ClipboardDocumentListIcon className="h-5 w-5" /> },
          { title: "Goods Receipts", path: "/goods-receipts", icon: <InboxArrowDownIcon className="h-5 w-5" />, retailOnly: true },
          { title: "Supplier Invoices", path: "/supplier-invoices", icon: <DocumentCheckIcon className="h-5 w-5" />, retailOnly: true },
          { title: "Credit Notes", path: "/credit-notes", icon: <ReceiptRefundIcon className="h-5 w-5" />, retailOnly: true },
          { title: "Purchase Returns", path: "/purchase-return", icon: <ArrowLeftIcon className="h-5 w-5" /> },
          { title: "Suppliers", path: "/suppliers", icon: <TruckIcon className="h-5 w-5" /> },
        ],
      },
      {
        section: "Employees",
        key: "employees",
        items: [
          { title: "Employee Management", path: "/client/employees", icon: <UsersIcon className="h-5 w-5" /> },
          { title: "Attendance", path: "/client/timesheet", icon: <ClockIcon className="h-5 w-5" /> },
        ],
      },
      {
        section: "Reports",
        key: "reports",
        items: [
          { title: "Z / X Reading", path: "/reports/z-reading", icon: <ChartBarIcon className="h-5 w-5" />, tenantTypes: ['restaurant'] },
          { title: "P-Mix Report", path: "/pos/menu-engineering", icon: <DocumentChartBarIcon className="h-5 w-5" />, tenantTypes: ['restaurant'] },
          { title: "Sales Reports", path: "/reports/sales", icon: <ShoppingCartIcon className="h-5 w-5" /> },
          { title: "Purchase Reports", path: "/reports/purchases", icon: <TruckIcon className="h-5 w-5" /> },
        ],
      },
    ],

    cashier: [
      {
        section: "Point of Sale",
        key: "point_of_sale",
        items: [
          { title: "POS Terminal", path: "/restaurant-pos", icon: <ShoppingCartIcon className="h-5 w-5" /> },
          { title: "POS", path: "/pos", icon: <BuildingStorefrontIcon className="h-5 w-5" />, tenantTypes: ['restaurant'], end: true },
          { title: "Menu", path: "/pos/menu", icon: <Squares2X2Icon className="h-5 w-5" />, tenantTypes: ['restaurant'] },
          { title: "Inventory", path: "/pos/inventory", icon: <InboxArrowDownIcon className="h-5 w-5" />, tenantTypes: ['restaurant'] },
          { title: "P-Mix Report", path: "/pos/menu-engineering", icon: <DocumentChartBarIcon className="h-5 w-5" />, tenantTypes: ['restaurant'] },
          { title: "Sales Orders", path: "/sales-order", icon: <ClipboardDocumentListIcon className="h-5 w-5" /> },
        ],
      },
      {
        section: "Operations",
        key: "operations",
        items: [{ title: "Tables", path: "/client/tables", icon: <Square3Stack3DIcon className="h-5 w-5" /> }],
      },
    ],
  };

  // NOTE: Helper to check if a section should be shown based on BOTH tenantType AND enabledModules
  const shouldShowSection = (key) => {
    if (!tenant) return true; // Show if tenant not loaded yet
    
    const tenantType = tenant.tenantType;
    
    // ACCOUNTING CLIENT section: ONLY for accounting_firm and mrwd tenantType
    if (key === 'accounting_client') {
      return isAccountingClient(tenantType);
    }

    // AO OPERATIONS section: ONLY for accounting_operations tenantType
    if (key === 'ao_operations') {
      return tenantType === 'accounting_operations';
    }

    // PM sections: ONLY for property_management tenantType
    if (key === 'pm_operations' || key === 'pm_reports' || key === 'pm_settings') {
      return tenantType === 'property_management';
    }

    // BILLING sections: ONLY for billing tenantType
    if (key === 'billing_accounting' || key === 'billing_operations' || key === 'billing_freight' || key === 'billing_trucking' || key === 'billing_reports' || key === 'billing_setup') {
      return tenantType === 'billing';
    }
    
    // ACCOUNTING section: ONLY when module enabled
    if (key === 'accounting') {
      return hasModule(tenant, 'accountingCore');
    }
    
    // ACCOUNTING REPORTS: ONLY when module enabled
    if (key === 'accounting_reports') {
      return hasModule(tenant, 'accountingReports');
    }
    
    // POINT OF SALE: ONLY when module enabled
    if (key === 'point_of_sale') {
      return hasModule(tenant, 'pos');
    }
    
    // INVENTORY: ONLY when module enabled
    if (key === 'inventory') {
      return hasModule(tenant, 'inventory');
    }
    
    // PURCHASING: ONLY when accountingCore enabled
    if (key === 'purchasing') {
      return hasModule(tenant, 'accountingCore');
    }
    
    // EMPLOYEES: ONLY when attendance module enabled
    if (key === 'employees') {
      return hasModule(tenant, 'attendance');
    }
    
    // REPORTS (parent section): show if accountingReports enabled
    if (key === 'reports') {
      return hasModule(tenant, 'accountingReports');
    }
    
    // SETTINGS: always shown
    if (key === 'settings') {
      return true;
    }
    
    return true; // Unknown sections shown by default
  };

  // NOTE: Helper to check if a single menu item should be shown
  const shouldShowMenuItem = (item) => {
    // schemes array: only show for tenants on one of the listed document schemes.
    // Deliberately keyed on the scheme, not the tenantType — enabling a scheme for
    // another tenant must never require touching this file (see
    // features/disbursements/schemes). Absent documentNumbering means LEGACY.
    if (item.schemes && Array.isArray(item.schemes)) {
      const scheme = tenant?.documentNumbering?.scheme || 'LEGACY';
      if (!item.schemes.includes(scheme)) return false;
    }

    // tenantTypes array: only show if tenant matches one of the listed types
    if (item.tenantTypes && Array.isArray(item.tenantTypes)) {
      if (!tenant) return false;
      if (!item.tenantTypes.includes(tenant.tenantType)) return false;
    }

    // retailOnly items: only show for retail tenantType
    if (item.retailOnly) {
      return tenant?.tenantType === 'retail';
    }

    if (!tenant) return true;
    
    // Map item paths to module requirements
    const pathModuleMap = {
      '/accounting-reports': 'accountingReports',
      '/client/general-ledger': 'accountingReports',
      '/journal-entry': 'accountingCore',
      '/disbursements': 'accountingCore',
      '/client/chart-of-accounts': 'accountingCore',
      '/client/chart-of-accounts-types': 'accountingCore',
      '/sales-order': 'pos',
      '/invoice': 'invoicing',
      '/inventory': 'inventory',
      '/client/products': 'inventory',
      '/client/product-category': 'inventory',
      '/purchase-order': 'accountingCore',
      '/goods-receipts': 'accountingCore',
      '/supplier-invoices': 'accountingCore',
      '/credit-notes': 'accountingCore',
      '/suppliers': 'accountingCore',
      '/client/timesheet': 'attendance',
      '/client/employees': 'attendance',
      '/client/tables': 'pos',
      '/customer': 'pos',
      '/client/stores': 'stores', // Special: handled below
    };

    const moduleKey = pathModuleMap[item.path];
    
    // Special handling for Stores: show if pos enabled OR tenantType in [retail, restaurant]
    if (moduleKey === 'stores') {
      const hasPos = hasModule(tenant, 'pos');
      const isRetailOrRestaurant = tenant.tenantType === 'retail' || tenant.tenantType === 'restaurant';
      return hasPos || isRetailOrRestaurant;
    }
    
    if (!moduleKey) return true; // Items without specific module requirement shown by default
    
    return hasModule(tenant, moduleKey);
  };

  // NOTE: Tenant-type filtering rules (now enhanced with strict module gating)
  const filteredRoleMenu = useMemo(() => {
    // billing_ff shares admin's section list; the billing tenantType filter below
    // trims it down to freight-forwarding + reports only.
    const effectiveRole = role === 'billing_ff' ? 'admin' : role;
    const base = roleBasedMenu[effectiveRole] || [];

    // Path -> Module requirement map for identifying module-gated items
    const modulGatedPaths = {
      '/accounting-reports': true,
      '/client/general-ledger': true,
      '/journal-entry': true,
      '/disbursements': true,
      '/client/chart-of-accounts': true,
      '/client/chart-of-accounts-types': true,
      '/sales-order': true,
      '/invoice': true,
      '/inventory': true,
      '/client/products': true,
      '/client/product-category': true,
      '/purchase-order': true,
      '/goods-receipts': true,
      '/supplier-invoices': true,
      '/credit-notes': true,
      '/suppliers': true,
      '/client/timesheet': true,
      '/client/employees': true,
      '/client/tables': true,
      '/customer': true,
      '/client/stores': true,
      '/sales': true,
      '/client/vendors': true,
      '/client/tax-obligation': true,
    };

    // No tenant yet → filter out module-gated items to prevent flash
    if (!tenant) {
      return base
        .map(group => {
          // tenantTypes-gated items (e.g. ATC Codes) also wait for the tenant. Superadmin
          // has no tenant, but its own menu items carry no tenantTypes, so they still show.
          const filteredItems = (group.items || []).filter(item => !modulGatedPaths[item.path] && !item.tenantTypes);
          if (filteredItems.length === 0) return null;
          return { ...group, items: filteredItems };
        })
        .filter(Boolean);
    }

    const tenantType = tenant.tenantType;
    // The per-tenant settings branches below skip shouldShowMenuItem, so they apply
    // item.tenantTypes (e.g. ATC Codes → accounting_firm only) through this instead.
    const matchesTenantType = (i) => !Array.isArray(i.tenantTypes) || i.tenantTypes.includes(tenantType);

    // For ACCOUNTING FIRM: show only accounting-related sections, remove all POS/retail sections
    if (tenantType === 'accounting_firm') {
      const disallowedSections = new Set([
        'point_of_sale',
        'inventory',
        'purchasing',
        'employees',
        'operations',
        'reports',     // accounting_firm uses dedicated "accounting_reports" section
        'ao_operations', // not for accounting_firm
      ]);

      return base
        .filter((group) => {
          if (!group.key) return true;
          if (disallowedSections.has(group.key)) return false;
          if (!shouldShowSection(group.key)) return false;
          return true;
        })
        .map((group) => {
          // For accounting firm, exclude Stores from settings (they don't use stores)
          if (group.key === 'settings') {
            const items = (group.items || []).filter((i) => i.path !== '/client/stores' && matchesTenantType(i));
            if (items.length === 0) return null;
            return { ...group, items };
          }
          
          // Filter items within each section based on module gating
          if (group.items && Array.isArray(group.items)) {
            const filteredItems = group.items.filter(shouldShowMenuItem);
            if (filteredItems.length === 0) return null;
            return { ...group, items: filteredItems };
          }
          
          return group;
        })
        .filter(Boolean);
    }

    // For ERP: similar to accounting_firm but hide tax_obligation, show customer, employees, vendors
    if (tenantType === 'mrwd') {
      const disallowedSections = new Set([
        'point_of_sale',
        'inventory',
        'purchasing',
        'operations',
        'reports', // mrwd uses dedicated "accounting_reports" section
        'ao_operations', // not for mrwd
      ]);

      return base
        .filter((group) => {
          if (!group.key) return true;
          if (disallowedSections.has(group.key)) return false;
          if (!shouldShowSection(group.key)) return false;
          return true;
        })
        .map((group) => {
          // accounting_client: hide tax_obligation only
          if (group.key === 'accounting_client') {
            const items = (group.items || []).filter(
              (i) => i.path !== '/client/tax-obligation'
            );
            if (items.length === 0) return null;
            return { ...group, items };
          }

          // settings: remove Stores (same as accounting_firm)
          if (group.key === 'settings') {
            const items = (group.items || []).filter((i) => i.path !== '/client/stores' && matchesTenantType(i));
            if (items.length === 0) return null;
            return { ...group, items };
          }

          // All other sections: apply standard module gating
          if (group.items && Array.isArray(group.items)) {
            const filteredItems = group.items.filter(shouldShowMenuItem);
            if (filteredItems.length === 0) return null;
            return { ...group, items: filteredItems };
          }

          return group;
        })
        .filter(Boolean);
    }

    // For BILLING tenantType: show only billing sections + settings
    if (tenantType === 'billing') {
      // billing_ff: COA + Journal Entries + General Ledger + FF Billing only (no Waybill, no reports)
      const billingKeys = role === 'billing_ff'
        ? new Set(['billing_accounting', 'billing_freight'])
        : new Set(['billing_accounting', 'billing_operations', 'billing_freight', 'billing_trucking', 'billing_reports', 'billing_setup', 'settings']);

      const BILLING_FF_ACCOUNTING_PATHS = new Set([
        '/client/chart-of-accounts',
        '/journal-entry',
        '/client/general-ledger',
      ]);

      return base
        .filter((group) => {
          if (!group.key) return false; // hide common items (dashboard handled in billing_accounting)
          return billingKeys.has(group.key);
        })
        .map((group) => {
          // billing_ff: strip billing_accounting to COA, JE, GL only
          if (group.key === 'billing_accounting' && role === 'billing_ff') {
            const items = (group.items || []).filter((i) => BILLING_FF_ACCOUNTING_PATHS.has(i.path));
            if (items.length === 0) return null;
            return { ...group, items };
          }
          // billing_ff: show only FF Billing, not Waybill
          if (group.key === 'billing_freight' && role === 'billing_ff') {
            const items = (group.items || []).filter((i) => i.path === '/billing/ff-billing');
            if (items.length === 0) return null;
            return { ...group, items };
          }
          if (group.key === 'settings') {
            const items = (group.items || []).filter((i) => i.path !== '/client/stores' && matchesTenantType(i));
            if (items.length === 0) return null;
            return { ...group, items };
          }
          return group;
        })
        .filter(Boolean);
    }

    // For PROPERTY MANAGEMENT tenantType: show only pm sections
    if (tenantType === 'property_management') {
      const pmKeys = new Set(['pm_operations', 'pm_reports', 'pm_settings']);
      return base
        .filter((group) => group.key && pmKeys.has(group.key))
        .filter(Boolean);
    }

    // For ACCOUNTING OPERATIONS tenantType: show only ao_operations section
    if (tenantType === 'accounting_operations') {
      return base
        .filter((group) => {
          if (!group.key) return true;
          if (group.key !== 'ao_operations' && group.key !== 'settings') return false;
          return shouldShowSection(group.key);
        })
        .map((group) => {
          // settings: remove Stores (accounting_operations doesn't use stores)
          if (group.key === 'settings') {
            const items = (group.items || []).filter((i) => i.path !== '/client/stores' && matchesTenantType(i));
            if (items.length === 0) return null;
            return { ...group, items };
          }
          return group;
        })
        .filter(Boolean);
    }

    // For RETAIL and RESTAURANT: show everything except "Accounting Client" section
    //  but apply module gating for conditional modules
    return base
      .filter((group) => {
        // Remove "Accounting Client" section for non-accounting_firm tenants
        if (group.key === 'accounting_client') return false;
        
        // Also remove the "accounting_reports" section (not typically used for retail/restaurant)
        // instead, use the main "reports" section
        if (group.key === 'accounting_reports') return false;

        // Never show ao_operations for retail/restaurant
        if (group.key === 'ao_operations') return false;
        
        // Apply module gating to remaining sections
        if (!shouldShowSection(group.key)) return false;
        
        return true;
      })
      .map((group) => {
        // Filter items within the section based on module gating
        if (group.items && Array.isArray(group.items)) {
          const filteredItems = group.items.filter(shouldShowMenuItem);
          if (filteredItems.length === 0) return null;
          return { ...group, items: filteredItems };
        }
        return group;
      })
      .filter(Boolean);
  }, [role, roleBasedMenu, tenant]);

  // Combine common and role-specific menu items
  const menuItems = useMemo(() => {
    return [
      ...commonMenu.filter((item) => item.roles.includes(role)),
      ...filteredRoleMenu,
    ];
  }, [commonMenu, role, filteredRoleMenu]);

  // Sync expandedSections to localStorage whenever tenant type changes (first login)
  useEffect(() => {
    if (!tenant) return;
    const alreadyCached = localStorage.getItem('sidebarSections');
    if (alreadyCached) return; // Respect user's existing preferences
    // First time for this tenant type: set smart defaults
    const defaults = tenant.tenantType === 'accounting_firm'
      ? { accounting_client: true, point_of_sale: false, accounting: true, inventory: false, purchasing: false, employees: false, reports: true, accounting_reports: true, settings: true, operations: false, ao_operations: false }
      : tenant.tenantType === 'mrwd'
      ? { accounting_client: true, point_of_sale: false, accounting: true, inventory: false, purchasing: false, employees: true, reports: false, accounting_reports: true, settings: true, operations: false, ao_operations: false }
      : tenant.tenantType === 'accounting_operations'
      ? { accounting_client: false, point_of_sale: false, accounting: false, inventory: false, purchasing: false, employees: false, reports: false, accounting_reports: false, settings: false, operations: false, ao_operations: true }
      : tenant.tenantType === 'billing' && role === 'billing_ff'
      ? { billing_freight: true, billing_accounting: true, billing_reports: false, settings: false, billing_operations: false, billing_trucking: false, billing_setup: false, accounting_client: false, point_of_sale: false, accounting: false, inventory: false, purchasing: false, employees: false, reports: false, accounting_reports: false, operations: false, ao_operations: false, pm_operations: false, pm_reports: false }
      : tenant.tenantType === 'billing'
      ? { billing_accounting: true, billing_operations: true, billing_freight: true, billing_trucking: true, billing_reports: true, billing_setup: true, settings: true, accounting_client: false, point_of_sale: false, accounting: false, inventory: false, purchasing: false, employees: false, reports: false, accounting_reports: false, operations: false, ao_operations: false, pm_operations: false, pm_reports: false }
      : tenant.tenantType === 'property_management'
      ? { pm_operations: true, pm_reports: true, pm_settings: true, accounting_client: false, point_of_sale: false, accounting: false, inventory: false, purchasing: false, employees: false, reports: false, accounting_reports: false, settings: false, operations: false, ao_operations: false, billing_accounting: false, billing_operations: false, billing_freight: false, billing_trucking: false, billing_reports: false, billing_setup: false }
      : { accounting_client: false, point_of_sale: true, accounting: true, inventory: true, purchasing: true, employees: true, reports: true, accounting_reports: false, settings: true, operations: true, ao_operations: false };
    setExpandedSections(defaults);
    localStorage.setItem('sidebarSections', JSON.stringify(defaults));
  }, [tenant]);

  return (
    <div
      className={[
        // Base styles
        "bg-gray-800 text-white p-4 flex flex-col shrink-0",
        // Mobile: fixed overlay, slide in/out, always w-64
        "fixed inset-y-0 left-0 z-40 w-64",
        "transition-transform duration-300 ease-in-out",
        mobileOpen ? "translate-x-0" : "-translate-x-full",
        // Desktop: static in the flex row, no transform, width toggle
        "md:relative md:inset-auto md:translate-x-0 md:transition-all md:duration-300",
        isOpen ? "md:w-64" : "md:w-20",
      ].join(" ")}
    >
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-large font-bold">{isOpen ? "Venta Portal" : "Venta"}</h1>

        {/* Collapse toggle — desktop only */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="hidden md:block p-1 rounded-md hover:bg-gray-700 transition-colors"
        >
          {isOpen ? (
            <ChevronLeftIcon className="h-5 w-5 text-gray-300" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-gray-300" />
          )}
        </button>
      </div>



      {/* Optional: show a subtle tenant type pill */}
      {(isOpen || mobileOpen) && tenant && (
        <div className="mb-3 px-2">
          <span className="text-[10px] px-2 py-1 rounded bg-gray-700 text-gray-200">
            {tenant.tenantType?.replace(/_/g, " ").toUpperCase()}
          </span>
        </div>
      )}

      <div className="flex-grow overflow-y-auto">
        {/* On mobile the sidebar is always full-width, so labels are always visible */}
        {menuItems.map((item, index) => {
          const showLabels = isOpen || mobileOpen;
          return (
          <div key={index} className="mb-4">
            {item.section ? (
              <>
                {showLabels && (
                  <div
                    className="flex items-center justify-between text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-2 cursor-pointer"
                    onClick={() => toggleSection(item.key || item.section.toLowerCase().replace(/ /g, "_"))}
                  >
                    <span>{item.section}</span>
                  </div>
                )}

                {(!isOpen || expandedSections[item.key || item.section.toLowerCase().replace(/ /g, "_")]) && (
                  <div className="space-y-1">
                    {item.items.map((subItem, subIndex) => {
                      // Hide items that require main-store access for sub-store users
                      if (subItem.requiresMainStore && !isMainStore) return null;
                      if (subItem.reportId) {
                        const isActiveReport =
                          location.pathname === '/accounting-reports' &&
                          currentReportTab === subItem.reportId;
                        return (
                          <ReportLinkItem
                            key={subIndex}
                            path={subItem.path}
                            title={subItem.title}
                            icon={subItem.icon}
                            isOpen={showLabels}
                            isActive={isActiveReport}
                            onMouseEnter={() => prefetchRoute(subItem.path)}
                          />
                        );
                      }
                      return (
                        <NavLinkItem
                          key={subIndex}
                          path={subItem.path}
                          title={subItem.title}
                          icon={subItem.icon}
                          isOpen={showLabels}
                          end={subItem.end}
                          onMouseEnter={() => prefetchRoute(subItem.path)}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <NavLinkItem
                path={item.path}
                title={item.title}
                icon={item.icon}
                isOpen={showLabels}
                end={item.end}
                onMouseEnter={() => prefetchRoute(item.path)}
              />
            )}
          </div>
          );
        })}
      </div>

      <div className="mt-auto pt-4 border-t border-gray-700">
        <button
          onClick={handleLogout}
          className={`flex items-center w-full p-2 rounded-lg hover:bg-gray-700 text-red-400 transition-colors ${
            !(isOpen || mobileOpen) ? "justify-center" : "space-x-2"
          }`}
        >
          <PowerIcon className="h-5 w-5" />
          {(isOpen || mobileOpen) && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;